// New conversation: choose a line that can text, enter the mobile number (validated to E.164), see its
// SMS consent on record, then write the first message. Nothing is sent without pressing Send, and a
// number without confirmed consent can't be texted first.
import { useEffect, useRef, useState } from 'react';
import { Dialog } from '../../ui/Dialog.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { useWorkspace, type PhoneNumber } from '../../state/workspace.tsx';
import { useRouter } from '../../lib/router.tsx';
import { hasPermission } from '../../lib/capabilities.ts';
import { parsePhoneInput, phoneParseMessage } from '../../lib/format.ts';
import { type ConsentRecord, MessagingClient, isAbortError } from '../../lib/api.ts';
import { describeError } from '../../lib/errorCopy.ts';
import type { MessagingController } from './controller.ts';
import { CONSENT_HINT, CONSENT_LABEL, type MessagingGate, SEND_PERMISSION, readConsent, sendBlock } from './model.ts';
import { CONSENT_ICON } from './icons.ts';
import { Composer } from './Composer.tsx';

/** Lines that can send SMS: the number's own sms capability flag, from the server. */
export function smsLines(numbers: readonly PhoneNumber[] | null | undefined): PhoneNumber[] {
  return (numbers ?? []).filter(n => n.capabilities.sms);
}

export type ConsentLookup =
  | { status: 'idle' }
  | { status: 'loading'; e164: string }
  | { status: 'ready'; e164: string; record: ConsentRecord }
  | { status: 'error'; e164: string; message: string };

export function NewConversation({ open, onClose, gate, controller }: { open: boolean; onClose: () => void; gate: MessagingGate; controller: MessagingController }) {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const [client] = useState(() => new MessagingClient(ws.api));
  const lines = smsLines(ws.numbers.value);
  const [lineId, setLineId] = useState<string>('');
  const [raw, setRaw] = useState('');
  const [touched, setTouched] = useState(false);
  const [consent, setConsent] = useState<ConsentLookup>({ status: 'idle' });
  const [nonce, setNonce] = useState(0);
  const lookup = useRef<AbortController | null>(null);

  const chosenLine = lines.find(l => l.id === lineId) ?? (lines.length === 1 ? lines[0] : undefined) ?? null;
  const parsed = parsePhoneInput(raw);

  // Look up consent once the number is valid; a changed number cancels the earlier lookup.
  const e164 = parsed.ok ? parsed.e164 : null;
  useEffect(() => {
    lookup.current?.abort();
    if (!open || !e164) { setConsent({ status: 'idle' }); return; }
    const abort = new AbortController();
    lookup.current = abort;
    setConsent({ status: 'loading', e164 });
    client.consent(e164, abort.signal).then(
      body => {
        if (abort.signal.aborted) return;
        const record = readConsent(body);
        setConsent(record ? { status: 'ready', e164, record } : { status: 'error', e164, message: 'The server sent a consent record this version can’t read.' });
      },
      error => {
        if (abort.signal.aborted || isAbortError(error)) return;
        // 404: no record at all, which is the same as no consent on record.
        if (typeof error === 'object' && error !== null && (error as { status?: number }).status === 404) {
          setConsent({ status: 'ready', e164, record: { state: 'unknown', source: null, disclosure_version: null, updated_at: null } });
          return;
        }
        setConsent({ status: 'error', e164, message: describeError(error, 'Couldn’t check this number’s SMS consent').title });
      },
    );
    return () => abort.abort();
  }, [open, e164, client, nonce]);

  const close = () => { setRaw(''); setTouched(false); setConsent({ status: 'idle' }); onClose(); };

  const block = sendBlock({ gate, canSend: hasPermission(ws.permissions, SEND_PERMISSION), consent: consent.status === 'ready' ? consent.record.state : null, first: true });
  const reason = block.blocked
    ? block.reason
    : !chosenLine
      ? (lines.length === 0 ? 'No line can send texts yet. An admin assigns a number with texting.' : 'Choose the line to text from.')
      : !parsed.ok
        ? 'Enter a valid mobile number.'
        : null;
  const blockCode = block.blocked ? block.code : !chosenLine ? 'line_required' : !parsed.ok ? 'invalid_e164' : null;

  return (
    <Dialog open={open} title="New conversation" onClose={close} labelledBy="newconv-title"
      footer={<button type="button" className="btn" onClick={close}>Close</button>}
    >
      <div className="newconv">
        <div className="field">
          <label htmlFor="newconv-line">Text from</label>
          {lines.length === 0 ? (
            <p id="newconv-line" className="inline-reason">{ws.numbers.loading ? 'Loading your lines…' : 'No line can send texts yet. An admin assigns a number with texting.'}</p>
          ) : (
            <select id="newconv-line" value={chosenLine?.id ?? ''} onChange={e => setLineId(e.target.value)}>
              {lines.length > 1 ? <option value="">Choose a line</option> : null}
              {lines.map(l => <option key={l.id} value={l.id}>{l.label} ({l.e164})</option>)}
            </select>
          )}
        </div>
        <div className="field">
          <label htmlFor="newconv-to">Mobile number</label>
          <input
            id="newconv-to"
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={raw}
            aria-invalid={touched && !parsed.ok ? true : undefined}
            aria-describedby="newconv-to-hint newconv-to-error"
            onChange={e => setRaw(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p id="newconv-to-hint" className="field-hint">Include the country code for numbers outside the US, for example +1 555 010 0142.</p>
          <p id="newconv-to-error" className="field-error">{touched && !parsed.ok ? phoneParseMessage(parsed) : ''}</p>
          {parsed.ok ? <p className="meta">Will text <span className="mono">{parsed.e164}</span></p> : null}
        </div>
        <ConsentStatus lookup={consent} onRetry={() => setNonce(n => n + 1)} />
        {open ? (
          <Composer
            key={`new:${chosenLine?.id ?? ''}:${e164 ?? ''}`}
            draftKey="new-conversation"
            idPrefix="newconv-msg"
            blockReason={reason}
            blockCode={blockCode}
            gate={gate}
            onSend={async (body, key) => {
              if (!chosenLine || !e164) return { ok: false, error: { code: 'invalid_e164', status: 0, outcomeUnknown: false, requestId: null } };
              const result = await controller.startThread({ number_id: chosenLine.id, remote_e164: e164, body }, key);
              if (result.ok) {
                close();
                navigate(`/messages/${encodeURIComponent(result.message.thread_id)}`);
              } else if (result.error.code === 'consent_opted_out') {
                setNonce(n => n + 1);
              }
              return result;
            }}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

/** The consent on record for the typed number, with its meaning. */
export function ConsentStatus({ lookup, onRetry }: { lookup: ConsentLookup; onRetry: () => void }) {
  switch (lookup.status) {
    case 'idle':
      return <p className="meta newconv-consent">SMS consent is checked once the number is complete.</p>;
    case 'loading':
      return <p className="meta newconv-consent" role="status">Checking SMS consent…</p>;
    case 'error':
      return (
        <div className="newconv-consent">
          <p className="field-error" role="alert">{lookup.message}</p>
          <button type="button" className="btn btn-small" onClick={onRetry}>Check again</button>
        </div>
      );
    case 'ready': {
      const state = lookup.record.state;
      return (
        <div className="newconv-consent" role="status">
          <p><Icon icon={CONSENT_ICON[state]} /> <strong>{CONSENT_LABEL[state]}</strong></p>
          <p className="meta">{CONSENT_HINT[state]}{lookup.record.disclosure_version ? ` Disclosure version ${lookup.record.disclosure_version}.` : ''}</p>
        </div>
      );
    }
  }
}
